import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { SeoContentAnalysis, ContentAnalysisStatus } from '@/types/api/seo';
import OptimizationChecklist from '@/components/OptimizationChecklist';
import * as Styles from './SeoContentDetail.styles';

type Props = {
  analysis: SeoContentAnalysis | null;
  isLoading: boolean;
  notFound: boolean;
};

const SeoContentDetailComponent = function ({
  analysis,
  isLoading,
  notFound,
}: Props): JSX.Element {
  const { projectId = '' } = useParams();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#054AD9' }} />
      </Box>
    );
  }

  if (notFound || !analysis) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          Content analysis not found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{}}>
      <Styles.PageHeader>
        <Box>
          <Styles.Title>Content Analysis</Styles.Title>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {analysis.url}
          </Typography>
        </Box>
        <Chip
          label={analysis.status}
          color={analysis.status === ContentAnalysisStatus.COMPLETE ? 'success' : 'default'}
          size="small"
        />
      </Styles.PageHeader>

      <Styles.ScoreSection>
        <Styles.StatCard>
          <Typography variant="caption" color="text.secondary">Overall Score</Typography>
          <Typography
            variant="h4"
            fontWeight={700}
            color={
              (analysis.overallScore ?? 0) >= 80
                ? 'success.main'
                : (analysis.overallScore ?? 0) >= 50
                  ? 'warning.main'
                  : 'error.main'
            }
          >
            {analysis.overallScore ?? '—'}
          </Typography>
        </Styles.StatCard>
        <Styles.StatCard>
          <Typography variant="caption" color="text.secondary">Target Keyword</Typography>
          <Typography variant="h6" fontWeight={600} sx={{ mt: 0.5 }}>
            {analysis.targetKeyword}
          </Typography>
        </Styles.StatCard>
      </Styles.ScoreSection>

      {analysis.results && (
        <Styles.ChecklistWrapper>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Optimization Checklist
          </Typography>
          <OptimizationChecklist results={analysis.results} />
        </Styles.ChecklistWrapper>
      )}
    </Box>
  );
};

export default SeoContentDetailComponent;
